/**
 * intakeDraftStorage — persistance brouillon d'intake projet en sessionStorage.
 *
 * Pourquoi sessionStorage et pas localStorage ?
 * ---------------------------------------------
 * - Un brouillon de projet est lié à l'onglet courant. Si l'utilisateur ouvre
 *   un 2ᵉ onglet pour comparer, il ne doit PAS hériter du brouillon de l'onglet
 *   1 (sinon il croit avoir perdu son travail).
 * - sessionStorage se purge à la fermeture de l'onglet → bonne hygiène
 *   (pas de brouillon orphelin qui traîne 3 mois).
 * - Acceptation perte au refresh : non, sessionStorage survit aux refresh
 *   de l'onglet — c'est exactement ce qu'on veut.
 *
 * Pourquoi un kind discriminant ?
 * -------------------------------
 * Aujourd'hui le seul intake est "mvp". Demain on aura un intake dynamique
 * (formulaire tenant-specific, voir _intake_mvp/README.md). Si les deux
 * partageaient une même clé, l'introduction du second resetrait
 * destructivement les drafts du premier au déploiement. Avec un kind
 * discriminant, on lit "le draft du kind demandé ou rien" — le draft d'un
 * autre kind est invisible (il sera supprimé par sessionStorage à la
 * fermeture, pas grave).
 *
 * Pourquoi un schema Zod à la lecture ?
 * -------------------------------------
 * Si quelqu'un édite manuellement le storage (devtools, navigateur tordu,
 * extension malveillante), on ne veut pas crasher l'app au mount avec un
 * `Cannot read property X of undefined`. On valide → si invalide, on rejette
 * silencieusement et on retourne null (ResumeDraftScreen affichera
 * directement l'écran initial).
 *
 * Sécurité (8.B)
 * --------------
 * On stocke EXCLUSIVEMENT :
 *   - UUIDs (company_id, contact_id, broker_id) — pas des données
 *   - codes catalogue (insurer_codes, guarantee_codes) — pas des données
 *   - texte saisi : title, reference, notes (max NOTE_MAX), tags
 *   - dates ISO yyyy-mm-dd
 *
 * On ne stocke PAS :
 *   - de credentials, tokens, JWT, signed URLs, API keys
 *   - de PII libre identifiante (le contact n'est référencé que par UUID)
 *   - de données médicales/financières (elles arriveront dans 8.C/9 via
 *     PDF, et NE devront PAS atterrir ici)
 *
 * Contre-mesure si quelqu'un essaie d'élargir : ce schema Zod en lecture
 * agit comme une porte fermée — un champ non listé est rejeté à la
 * relecture, donc le développeur saura qu'il faut soit étendre le schema
 * intentionnellement, soit ne pas stocker la donnée.
 */

import { z } from "zod";

const STORAGE_KEY = "kollectivia.intake.project.draft.v1";
const SCHEMA_VERSION = 1;

/**
 * Schéma de la donnée intake MVP — VALIDÉ à la lecture. Tous champs
 * optionnels (un brouillon peut être à n'importe quelle étape de remplissage).
 */
const IntakeMvpDataSchema = z.object({
  // Step 1
  company_id: z.string().uuid().optional(),

  // Step 2
  contact_id: z.string().uuid().optional(),
  broker_id: z.string().uuid().optional(),

  // Step 3
  title: z.string().max(200).optional(),
  reference: z.string().max(50).optional(),
  effect_date: z.string().optional(),
  renewal_target_date: z.string().optional(),
  deadline_date: z.string().optional(),
  insurer_codes: z.array(z.string()).max(10).optional(),
  guarantee_codes: z.array(z.string()).max(30).optional(),
  notes: z.string().max(5000).optional(),
  tags: z.array(z.string().max(50)).max(20).optional(),
});

export type IntakeMvpData = z.infer<typeof IntakeMvpDataSchema>;

const DraftEnvelopeSchema = z.object({
  intakeKind: z.literal("mvp"),
  version: z.literal(SCHEMA_VERSION),
  step: z.number().int().min(1).max(3),
  updatedAt: z.string(),
  data: IntakeMvpDataSchema,
});

export type IntakeMvpDraft = z.infer<typeof DraftEnvelopeSchema>;

// -----------------------------------------------------------------------------
// Accès sessionStorage robuste
// -----------------------------------------------------------------------------
// `window.sessionStorage` peut throw en mode privé Safari, en quota exceeded,
// ou si le code tourne dans un contexte SSR. On encapsule.

function safeGetItem(key: string): string | null {
  try {
    return typeof window === "undefined" ? null : window.sessionStorage.getItem(key);
  } catch {
    return null;
  }
}

function safeSetItem(key: string, value: string): boolean {
  try {
    if (typeof window === "undefined") return false;
    window.sessionStorage.setItem(key, value);
    return true;
  } catch {
    return false;
  }
}

function safeRemoveItem(key: string): void {
  try {
    if (typeof window === "undefined") return;
    window.sessionStorage.removeItem(key);
  } catch {
    /* swallow — best-effort */
  }
}

// -----------------------------------------------------------------------------
// API publique
// -----------------------------------------------------------------------------

/**
 * Charge le brouillon courant pour le kind donné. Retourne null si :
 *   - storage indisponible
 *   - aucun brouillon présent
 *   - JSON corrompu
 *   - schema mismatch (champs ajoutés à la main, version différente, autre kind)
 *
 * En cas de mismatch on supprime silencieusement la valeur foireuse — pas
 * de log, pas de toast, juste fresh start. C'est le comportement le moins
 * surprenant pour l'utilisateur.
 */
export function loadIntakeDraft(kind: "mvp"): IntakeMvpDraft | null {
  const raw = safeGetItem(STORAGE_KEY);
  if (!raw) return null;

  let parsed: unknown;
  try {
    parsed = JSON.parse(raw);
  } catch {
    safeRemoveItem(STORAGE_KEY);
    return null;
  }

  const result = DraftEnvelopeSchema.safeParse(parsed);
  if (!result.success) {
    safeRemoveItem(STORAGE_KEY);
    return null;
  }

  if (result.data.intakeKind !== kind) {
    // Brouillon d'un autre kind (ex. dynamic plus tard) — on ne touche pas.
    return null;
  }

  return result.data;
}

/**
 * Persiste un brouillon. Best-effort : si sessionStorage est indisponible
 * (Safari privé, quota), on retourne false sans crasher — l'utilisateur
 * verra juste un wizard sans persistence, ce qui est meilleur qu'une page
 * blanche.
 */
export function saveIntakeDraft(
  kind: "mvp",
  step: 1 | 2 | 3,
  data: IntakeMvpData,
): boolean {
  const envelope: IntakeMvpDraft = {
    intakeKind: kind,
    version: SCHEMA_VERSION,
    step,
    updatedAt: new Date().toISOString(),
    data,
  };

  // Validation avant écriture — défensif, attrape une régression si on étend
  // IntakeMvpData sans réfléchir et qu'on injecte un champ non sérialisable.
  const valid = DraftEnvelopeSchema.safeParse(envelope);
  if (!valid.success) return false;

  return safeSetItem(STORAGE_KEY, JSON.stringify(envelope));
}

/**
 * Supprime le brouillon courant. À appeler après submit réussi OU quand
 * l'utilisateur clique "Recommencer" dans le ResumeDraftScreen.
 */
export function clearIntakeDraft(): void {
  safeRemoveItem(STORAGE_KEY);
}

/**
 * Vrai si le brouillon a au moins un champ rempli — utilisé par le
 * navigation guard pour ne pas alerter quand le formulaire est vide.
 */
export function isDraftDirty(data: IntakeMvpData | undefined): boolean {
  if (!data) return false;
  return (
    !!data.company_id ||
    !!data.contact_id ||
    !!data.broker_id ||
    !!data.title?.trim() ||
    !!data.reference?.trim() ||
    !!data.effect_date ||
    !!data.renewal_target_date ||
    !!data.deadline_date ||
    (data.insurer_codes?.length ?? 0) > 0 ||
    (data.guarantee_codes?.length ?? 0) > 0 ||
    !!data.notes?.trim() ||
    (data.tags?.length ?? 0) > 0
  );
}
